import React, {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useCallback,
    useState,
} from "react";
import { Song, Playlist } from "@/types/types";
import { useUser } from "@/context/userContext";
import { usePlaybackSettings } from "@/context/PlaybackSettingsContext";
import { markRecentlyJoinedPlaylistOffline } from "@/utils/recentlyJoinedPlaylists";
import { addSessionActivity } from "@/utils/recentActivities";
import { resolvePlaylistImage } from "@/utils/imageUtils";
import { getReadableSongMetadata } from "@/utils/songMetadata";
import { normalizePlatformValue } from "@/utils/platform";
import { isMixDuration } from "@/utils/songDuration";
import { markSessionRecentlyLeft, wasSessionRecentlyLeft } from "@/utils/sessionLeaveGuard";
import { scheduleSessionEndPageRefresh } from "@/utils/sessionEndRefresh";
import { applyCompactVoteUpdate, shouldReconcileQueueRevision } from "@/utils/sessionVoteQueue";
import { shouldReconcileTransientEmptyQueue } from "@/utils/sessionQueueSnapshot";
import { getSessionBootstrapRetryPlan } from "@/utils/sessionBootstrapRetry";
import {
    SESSION_TRANSITION_REQUEST_EVENT,
    type SessionTransitionRequestDetail,
} from "@/utils/sessionTransitionEvents";
import { readCachedSessionSnapshot } from "@/utils/sessionSnapshotCache";
import { toast } from "@/components/ui/use-toast";
import { logger } from "@/lib/logger";
import { useSocket } from "@/context/SocketContext";
// ---------- Types ----------
interface SessionState {
    currentPlaylist: Playlist | null;
    sessionId: string | null;
    currentSong: Song | null;
    isPlaying: boolean;
    sessionActive: boolean;
    sessionInfo: SessionInfo | null;
}

export interface SessionSettings {
    allow_guest_add: boolean;
    min_votes: number;
    min_votes_to_add: number;
    min_celebration_votes: number;
    auto_play_next: boolean;
    play_all_songs_by_votes: boolean;
    shuffle_on_start: boolean;
    is_public: boolean;
    require_approval: boolean;
    copy_public_playlist: boolean;
    copied_playlist_name: string;
    save_session_additions: boolean;
    solo_voting_cooldown_enabled: boolean;
    queue_revision?: number;
    transition_audio_url?: string;
    song_end_offset_percent: 0 | 10 | 15 | 25;
}

export interface SessionInfo {
    id: string;
    name: string;
    hostName: string;
    hostId: string;
    playlistId?: string;
    users?: any[];
    is_host?: boolean;
    can_edit?: boolean;
    can_add_to_queue?: boolean;
    can_suggest?: boolean;
    user_in_session?: boolean;
    songCount: number;
    participants: number;
    settings?: SessionSettings;
}

type SessionAction =
    | {
        type: "SET_FULL_SESSION"; payload: {
            playlist: Playlist;
            sessionInfo: SessionInfo;
            sessionId: string;
            currentSong?: Song | null;
            isPlaying?: boolean;
        }
    }
    | {
        type: "RESTORE_CACHED_SESSION"; payload: {
            playlist: Playlist;
            sessionInfo: SessionInfo;
            sessionId: string;
        }
    }
    | { type: "SET_SESSION"; payload: { playlist: Playlist; sessionId: string } }
    | { type: "CLEAR_SESSION" }
    | { type: "ADD_SONG"; payload: Song }
    | { type: "SET_SONGS"; payload: Song[] }
    | { type: "UPDATE_SONG_VOTES"; payload: { songId: string; votes: number } }
    | { type: "APPLY_COMPACT_VOTE"; payload: { songId: string; votes: number; position: number } }
    | { type: "SET_CURRENT_SONG"; payload: Song | null }
    | { type: "SET_IS_PLAYING"; payload: boolean }
    | { type: "UPDATE_PARTICIPANTS"; payload: number }
    | { type: "UPDATE_SESSION_INFO"; payload: Partial<SessionInfo> }
    | {
        type: "UPDATE_PLAYLIST_STATS";
        payload: {
            likes?: number;
            like_count?: number;
            likes_count?: number;
            ratings?: number;
            rating?: number;
            comments?: number;
            comment_count?: number;
            comments_count?: number;
            liked_by_user?: boolean;
        }
    }
    | { type: "SET_SESSION_ACTIVE"; payload: boolean }
    | {
        type: "UPDATE_ALL_SONGS";
        payload: Array<{ id?: string; song_id?: string; votes: number; title?: string; added_at?: string | null }>;
    };

const DEFAULT_SESSION_SETTINGS: SessionSettings = {
    allow_guest_add: true,
    min_votes: 1,
    min_votes_to_add: 1,
    min_celebration_votes: 3,
    auto_play_next: false,
    play_all_songs_by_votes: false,
    shuffle_on_start: false,
    is_public: true,
    require_approval: false,
    copy_public_playlist: false,
    copied_playlist_name: "",
    save_session_additions: false,
    solo_voting_cooldown_enabled: false,
    song_end_offset_percent: 0,
};

const normalizeSessionSettings = (rawSettings: any): SessionSettings => {
    let source = rawSettings;
    if (typeof source === "string") {
        try {
            source = JSON.parse(source);
        } catch {
            source = {};
        }
    }
    if (!source || typeof source !== "object") {
        source = {};
    }

    const toBoolean = (value: unknown, fallback: boolean): boolean => {
        if (typeof value === "boolean") return value;
        if (typeof value === "number") return value !== 0;
        if (typeof value === "string") {
            const normalized = value.trim().toLowerCase();
            if (["true", "1", "yes", "on"].includes(normalized)) return true;
            if (["false", "0", "no", "off"].includes(normalized)) return false;
        }
        return fallback;
    };

    const rawAllowGuestAdd = source?.allow_guest_add ?? source?.allowGuestAdd;
    const rawMinVotes = source?.min_votes ?? source?.minVote;
    const rawMinVotesToAdd = source?.min_votes_to_add ?? source?.minVoteToAdd;
    const rawMinCelebrationVotes = source?.min_celebration_votes ?? source?.minCelebrationVotes;
    const rawShuffleOnStart = source?.shuffle_on_start ?? source?.shuffleOnStart;
    const rawPlayAllSongsByVotes = source?.play_all_songs_by_votes ?? source?.playAllSongsByVotes;
    const rawIsPublic = source?.is_public ?? source?.isPublic;
    const rawRequireApproval = source?.require_approval ?? source?.requireApproval;
    const rawCopyPublicPlaylist = source?.copy_public_playlist ?? source?.copyPublicPlaylist;
    const rawCopiedPlaylistName = source?.copied_playlist_name ?? source?.copiedPlaylistName;
    const rawSaveSessionAdditions = source?.save_session_additions ?? source?.saveSessionAdditions;
    const rawSoloVotingCooldownEnabled = source?.solo_voting_cooldown_enabled ?? source?.soloVotingCooldownEnabled;
    const rawTransitionAudioUrl = source?.transition_audio_url ?? source?.transitionAudioUrl;
    const parsedSongEndOffset = Number(source?.song_end_offset_percent ?? source?.songEndOffsetPercent ?? 0);
    const rawQueueRevision = source?.queue_revision ?? source?.queueRevision;
    const parsedMinVotes = Number(rawMinVotes);
    const normalizedMinVotes =
        Number.isFinite(parsedMinVotes) && parsedMinVotes >= 0
            ? parsedMinVotes
            : DEFAULT_SESSION_SETTINGS.min_votes;
    const parsedMinVotesToAdd = Number(rawMinVotesToAdd);
    const parsedMinCelebrationVotes = Number(rawMinCelebrationVotes);
    const parsedQueueRevision = Number(rawQueueRevision);
    return {
        allow_guest_add: toBoolean(
            rawAllowGuestAdd,
            DEFAULT_SESSION_SETTINGS.allow_guest_add
        ),
        min_votes: normalizedMinVotes,
        min_votes_to_add:
            rawMinVotesToAdd !== undefined &&
            rawMinVotesToAdd !== null &&
            Number.isFinite(parsedMinVotesToAdd) &&
            parsedMinVotesToAdd >= 0
                ? parsedMinVotesToAdd
                : normalizedMinVotes,
        min_celebration_votes:
            Number.isFinite(parsedMinCelebrationVotes) && parsedMinCelebrationVotes >= 1
                ? parsedMinCelebrationVotes
                : DEFAULT_SESSION_SETTINGS.min_celebration_votes,
        auto_play_next: false,
        play_all_songs_by_votes: toBoolean(rawPlayAllSongsByVotes, DEFAULT_SESSION_SETTINGS.play_all_songs_by_votes),
        shuffle_on_start: toBoolean(
            rawShuffleOnStart,
            DEFAULT_SESSION_SETTINGS.shuffle_on_start
        ),
        is_public: toBoolean(
            rawIsPublic,
            DEFAULT_SESSION_SETTINGS.is_public
        ),
        require_approval: toBoolean(
            rawRequireApproval,
            DEFAULT_SESSION_SETTINGS.require_approval
        ),
        copy_public_playlist: toBoolean(rawCopyPublicPlaylist, DEFAULT_SESSION_SETTINGS.copy_public_playlist),
        copied_playlist_name: String(rawCopiedPlaylistName || "").trim(),
        save_session_additions: toBoolean(rawSaveSessionAdditions, DEFAULT_SESSION_SETTINGS.save_session_additions),
        solo_voting_cooldown_enabled: toBoolean(rawSoloVotingCooldownEnabled, DEFAULT_SESSION_SETTINGS.solo_voting_cooldown_enabled),
        transition_audio_url: String(rawTransitionAudioUrl || "").trim() || undefined,
        song_end_offset_percent: [10, 15, 25].includes(parsedSongEndOffset)
            ? parsedSongEndOffset as 10 | 15 | 25
            : 0,
        queue_revision: Number.isInteger(parsedQueueRevision) && parsedQueueRevision >= 0
            ? parsedQueueRevision
            : 0,
    };
};

const parseSessionSettingsPayload = (rawSettings: unknown): Partial<SessionSettings> => {
    let source = rawSettings;
    if (typeof source === "string") {
        try {
            source = JSON.parse(source);
        } catch {
            return {};
        }
    }
    if (!source || typeof source !== "object") {
        return {};
    }
    return source as Partial<SessionSettings>;
};

const isSameSongIdentity = (
    left: Partial<Song> | null | undefined,
    right: Partial<Song> | null | undefined
): boolean => {
    const leftId = String(left?.id || "").trim();
    const rightId = String(right?.id || "").trim();
    if (leftId && rightId && leftId === rightId) return true;

    const leftUrl = String(left?.url || "").trim();
    const rightUrl = String(right?.url || "").trim();
    if (leftUrl && rightUrl && leftUrl === rightUrl) return true;

    return false;
};

const hasPlayableSongSource = (song: Partial<Song> | null | undefined): boolean => {
    return Boolean(
        song?.url ||
        (song as any)?.link ||
        (song as any)?.stream_url ||
        (song as any)?.source_url
    );
};

const hydrateSongWithPlaylistData = (song: Song | null, playlistSongs: Song[]): Song | null => {
    if (!song) return null;

    const hasIdentity = Boolean(
        pickString(song.id, (song as any)?.song_id, song.url, (song as any)?.link)
    );

    const matchedSong = playlistSongs.find((candidate) => {
        if (isSameSongIdentity(candidate, song)) return true;

        if (hasIdentity) return false;

        const candidateTitle = pickString(candidate.title).toLowerCase();
        const songTitle = pickString(song.title).toLowerCase();
        const candidateArtist = pickString(candidate.artist).toLowerCase();
        const songArtist = pickString(song.artist).toLowerCase();

        return Boolean(candidateTitle && songTitle && candidateTitle === songTitle && candidateArtist === songArtist);
    });

    if (!matchedSong) return song;

    return {
        ...matchedSong,
        ...song,
        url: song.url || matchedSong.url || "",
        image:
            (song.image && song.image !== "/default-song.png" ? song.image : "") ||
            matchedSong.image ||
            song.image,
        platform: song.platform || matchedSong.platform || "youtube",
        duration: song.duration || matchedSong.duration || 0,
        votes: song.votes ?? matchedSong.votes ?? 0,
    };
};

// ---------- Reducer ----------
function sessionReducer(state: SessionState, action: SessionAction): SessionState {
    switch (action.type) {
        case "SET_FULL_SESSION": {
            return {
                ...state,
                currentPlaylist: action.payload.playlist,
                sessionId: action.payload.sessionId,
                sessionInfo: action.payload.sessionInfo,
                currentSong: action.payload.currentSong ?? null,
                isPlaying: Boolean(action.payload.isPlaying),
                sessionActive: true,
            };
        }
        case "RESTORE_CACHED_SESSION": {
            const isSameSession = state.sessionId === action.payload.sessionId;
            return {
                ...state,
                currentPlaylist: action.payload.playlist,
                sessionId: action.payload.sessionId,
                sessionInfo: action.payload.sessionInfo,
                currentSong: isSameSession ? state.currentSong : null,
                isPlaying: isSameSession ? state.isPlaying : false,
                sessionActive: true,
            };
        }
        case "SET_SESSION": {
            const sessionInfo: SessionInfo = {
                id: action.payload.sessionId,
                name: action.payload.playlist.title,
                hostName: "You",
                hostId: "",
                is_host: true,
                songCount: action.payload.playlist.songs?.length || 0,
                participants: 1,
                settings: DEFAULT_SESSION_SETTINGS,
            };
            return {
                ...state,
                currentPlaylist: action.payload.playlist,
                sessionId: action.payload.sessionId,
                sessionInfo,
                sessionActive: true,
            };
        }
        case "CLEAR_SESSION": {
            return {
                currentPlaylist: null,
                sessionId: null,
                currentSong: null,
                isPlaying: false,
                sessionActive: false,
                sessionInfo: null,
            };
        }
        case "SET_SESSION_ACTIVE": {
            return {
                ...state,
                sessionActive: action.payload,
            };
        }
        case "ADD_SONG": {
            if (!state.currentPlaylist) return state;

            // Check if song already exists (by ID or URL)
            const songExists = state.currentPlaylist.songs.some(
                s => s.id === action.payload.id || s.url === action.payload.url
            );

            if (songExists) {
                return state;
            }

            const newSongs = [...state.currentPlaylist.songs, action.payload];

            return {
                ...state,
                currentPlaylist: { ...state.currentPlaylist, songs: newSongs },
                sessionInfo: state.sessionInfo
                    ? { ...state.sessionInfo, songCount: newSongs.length }
                    : null,
            };
        }
        case "SET_SONGS": {
            if (!state.currentPlaylist) return state;
            return {
                ...state,
                currentPlaylist: { ...state.currentPlaylist, songs: action.payload },
                sessionInfo: state.sessionInfo
                    ? { ...state.sessionInfo, songCount: action.payload.length }
                    : null,
            };
        }
        case "UPDATE_SONG_VOTES": {
            if (!state.currentPlaylist) return state;
            const updatedSongs = state.currentPlaylist.songs.map((s) =>
                s.id === action.payload.songId ? { ...s, votes: action.payload.votes } : s
            );
            return {
                ...state,
                currentPlaylist: { ...state.currentPlaylist, songs: updatedSongs },
            };
        }
        case "APPLY_COMPACT_VOTE": {
            if (!state.currentPlaylist) return state;
            return {
                ...state,
                currentPlaylist: {
                    ...state.currentPlaylist,
                    songs: applyCompactVoteUpdate(
                        state.currentPlaylist.songs,
                        action.payload.songId,
                        action.payload.votes,
                        action.payload.position,
                        state.currentSong?.id,
                    ),
                },
            };
        }
        case "SET_CURRENT_SONG": {
            return {
                ...state,
                currentSong: action.payload,
            };
        }
        case "SET_IS_PLAYING": {
            return { ...state, isPlaying: action.payload };
        }
        case "UPDATE_PARTICIPANTS": {
            return {
                ...state,
                sessionInfo: state.sessionInfo
                    ? { ...state.sessionInfo, participants: action.payload }
                    : null,
            };
        }

        case "UPDATE_ALL_SONGS": {
            if (!state.currentPlaylist) return state;

            // Create vote and incoming-order maps from backend reordered payload.
            // Backend already sorts by votes desc + added_at asc, so ties should follow this order.
            const voteMap = new Map<string, number>();
            const incomingOrderMap = new Map<string, number>();
            const addedAtMap = new Map<string, string>();
            action.payload.forEach((songData, index) => {
                const songId = String(songData.id || songData.song_id || "").trim();
                if (!songId) return;
                voteMap.set(songId, Number(songData.votes ?? 0));
                if (!incomingOrderMap.has(songId)) {
                    incomingOrderMap.set(songId, index);
                }
                if (songData.added_at) {
                    addedAtMap.set(songId, String(songData.added_at));
                }
            });

            // Preserve existing order as a fallback tie-breaker for entries missing from payload.
            const previousOrderMap = new Map<string, number>();
            state.currentPlaylist.songs.forEach((song, index) => {
                const songId = String(song.id || (song as any).song_id || "").trim();
                if (!songId || previousOrderMap.has(songId)) return;
                previousOrderMap.set(songId, index);
            });

            // Update all songs with new votes.
            // Use explicit map existence checks so `0` votes are not dropped by falsy coercion.
            const updatedSongs = state.currentPlaylist.songs.map(song => {
                const primaryId = String(song.id || "").trim();
                const fallbackId = String((song as any).song_id || "").trim();
                let mappedVotes: number | undefined;

                if (primaryId && voteMap.has(primaryId)) {
                    mappedVotes = voteMap.get(primaryId);
                } else if (fallbackId && voteMap.has(fallbackId)) {
                    mappedVotes = voteMap.get(fallbackId);
                }

                const newVotes = mappedVotes ?? song.votes ?? 0;
                const mappedAddedAt =
                    (primaryId && addedAtMap.get(primaryId)) ||
                    (fallbackId && addedAtMap.get(fallbackId));
                return {
                    ...song,
                    votes: newVotes,
                    added_at: mappedAddedAt || (song as any).added_at,
                };
            });

            // Sort by votes descending, and for tied votes preserve first-come-first-served.
            const sortedSongs = [...updatedSongs].sort((a, b) => {
                const aVotes = Number(a.votes ?? 0);
                const bVotes = Number(b.votes ?? 0);
                if (bVotes !== aVotes) return bVotes - aVotes;

                const aId = String(a.id || (a as any).song_id || "").trim();
                const bId = String(b.id || (b as any).song_id || "").trim();

                const aIncoming = incomingOrderMap.get(aId);
                const bIncoming = incomingOrderMap.get(bId);
                const aIncomingOrder = aIncoming ?? Number.MAX_SAFE_INTEGER;
                const bIncomingOrder = bIncoming ?? Number.MAX_SAFE_INTEGER;
                if (aIncomingOrder !== bIncomingOrder) return aIncomingOrder - bIncomingOrder;

                const aPreviousOrder = previousOrderMap.get(aId) ?? Number.MAX_SAFE_INTEGER;
                const bPreviousOrder = previousOrderMap.get(bId) ?? Number.MAX_SAFE_INTEGER;
                if (aPreviousOrder !== bPreviousOrder) return aPreviousOrder - bPreviousOrder;

                return aId.localeCompare(bId);
            });

            return {
                ...state,
                currentPlaylist: { ...state.currentPlaylist, songs: sortedSongs },
            };
        }

        case "UPDATE_SESSION_INFO": {
            if (!state.sessionInfo) {
                return state;
            }
            const incomingSettings = action.payload.settings !== undefined
                ? normalizeSessionSettings({
                    ...(state.sessionInfo.settings || DEFAULT_SESSION_SETTINGS),
                    ...parseSessionSettingsPayload(action.payload.settings),
                })
                : state.sessionInfo.settings;

            return {
                ...state,
                sessionInfo: {
                    ...state.sessionInfo,
                    ...action.payload,
                    settings: incomingSettings,
                },
            };
        }
        case "UPDATE_PLAYLIST_STATS": {
            if (!state.currentPlaylist) {
                return state;
            }

            const nextLikes = Number(
                action.payload.likes ??
                action.payload.like_count ??
                action.payload.likes_count ??
                state.currentPlaylist.likes ??
                0
            );
            const nextComments = Number(
                action.payload.comments ??
                action.payload.comment_count ??
                action.payload.comments_count ??
                state.currentPlaylist.comments ??
                state.currentPlaylist.comment_count ??
                state.currentPlaylist.comments_count ??
                0
            );
            const nextRatings = Number(
                action.payload.ratings ??
                action.payload.rating ??
                state.currentPlaylist.ratings ??
                0
            );

            return {
                ...state,
                currentPlaylist: {
                    ...state.currentPlaylist,
                    likes: Number.isFinite(nextLikes) ? Math.max(0, nextLikes) : 0,
                    like_count: Number.isFinite(nextLikes) ? Math.max(0, nextLikes) : 0,
                    likes_count: Number.isFinite(nextLikes) ? Math.max(0, nextLikes) : 0,
                    liked_by_user:
                        typeof action.payload.liked_by_user === "boolean"
                            ? action.payload.liked_by_user
                            : state.currentPlaylist.liked_by_user,
                    comments: Number.isFinite(nextComments) ? Math.max(0, nextComments) : 0,
                    comment_count: Number.isFinite(nextComments) ? Math.max(0, nextComments) : 0,
                    comments_count: Number.isFinite(nextComments) ? Math.max(0, nextComments) : 0,
                    ratings: Number.isFinite(nextRatings) ? Math.max(0, Math.min(5, nextRatings)) : 0,
                },
            };
        }
        default: {
            return state;
        }
    }
}


// ---------- Context ----------
interface SessionContextType extends SessionState {
    pusher: any;
    channel: any;
    socket?: any;
    setSession: (playlist: Playlist, sessionId: string) => void;
    setFullSession: (sessionData: any) => void; 
    clearSession: () => void;
    setCurrentSong: (song: Song | null) => void;
    setIsPlaying: (isPlaying: boolean) => void;
    setSessionActive: (active: boolean) => void;
    loadFullSessionInfo: (sessionId: string) => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export interface SessionIdentityState {
    sessionId: string | null;
    sessionActive: boolean;
    sessionInfo: SessionInfo | null;
}

export interface SessionQueueState {
    currentPlaylist: Playlist | null;
    currentSong: Song | null;
    isPlaying: boolean;
}

export interface SessionPlaybackState {
    currentSong: Song | null;
    isPlaying: boolean;
}

export interface SessionConnectionState {
    pusher: any;
    channel: any;
    socket?: any;
}

export interface SessionActions {
    setSession: (playlist: Playlist, sessionId: string) => void;
    setFullSession: (sessionData: any) => void;
    clearSession: () => void;
    setCurrentSong: (song: Song | null) => void;
    setIsPlaying: (isPlaying: boolean) => void;
    setSessionActive: (active: boolean) => void;
    loadFullSessionInfo: (sessionId: string) => Promise<void>;
}

const SessionIdentityContext = createContext<SessionIdentityState | undefined>(undefined);
const SessionQueueContext = createContext<SessionQueueState | undefined>(undefined);
const SessionIdContext = createContext<string | null | undefined>(undefined);
const SessionPlaylistContext = createContext<Playlist | null | undefined>(undefined);
const SessionPlaybackContext = createContext<SessionPlaybackState | undefined>(undefined);
const SessionConnectionContext = createContext<SessionConnectionState | undefined>(undefined);
const SessionActionsContext = createContext<SessionActions | undefined>(undefined);

// Helper functions
const normalizeAssetUrl = (value: unknown): string => {
    if (typeof value !== "string") return "";
    const trimmed = value.trim();
    if (!trimmed) return "";

    if (/^(https?:\/\/|data:image|blob:)/i.test(trimmed)) {
        return trimmed;
    }

    const cleaned = trimmed.replace(/\\/g, "/").replace(/^\/+/, "");
    if (!cleaned) return "";

    if (
        cleaned.startsWith("api/") ||
        cleaned.startsWith("uploads/") ||
        cleaned.startsWith("static/")
    ) {
        return `/${cleaned}`;
    }

    if (/^[^/]+\.(png|jpe?g|gif|webp|bmp|svg|avif)$/i.test(cleaned)) {
        return `/uploads/logos/${cleaned}`;
    }

    return `/${cleaned}`;
};

export const createSongFromData = (s: any): Song => {
    const inferredPlatform = normalizePlatformValue(
        s.platform || s.stream_domain || s.stream,
        s.url || s.link || s.stream_url || s.source_url || ""
    );
    const platform = inferredPlatform === "other" ? "youtube" : inferredPlatform;
    const readableMetadata = getReadableSongMetadata(
        s.title || "Untitled",
        s.artist || "Unknown Artist",
        platform
    );

    return {
        id: s.id || s.song_id || "",
        title: readableMetadata.title,
        artist: readableMetadata.artist,
        url: s.url || s.link || s.stream_url || s.source_url || "",
        image: normalizeAssetUrl(s.image || s.logo_url || s.thumb_url) || "/default-song.png",
        duration: s.duration || 0,
        platform,
        votes: s.votes || 0,
        hot: false,
        audioSrc: s.url || s.link || s.stream_url || s.source_url || "",
        added_at: s.added_at || s.created_at || undefined,
    };
};

const pickString = (...candidates: unknown[]): string => {
    for (const candidate of candidates) {
        if (typeof candidate === "string") {
            const value = candidate.trim();
            if (value) return value;
        }
    }
    return "";
};

const looksLikeIdentifier = (value: string): boolean => {
    if (!value) return false;
    if (/^[0-9a-f]{24,}$/i.test(value)) return true;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) {
        return true;
    }
    return /^(usr|user|guest|ven|session|sid|id|playlist|plist|plst)_[a-z0-9]+$/i.test(value);
};

const isUsablePlaylistImage = (value: unknown): value is string => {
    if (typeof value !== "string") return false;
    const normalized = value.trim().toLowerCase();
    if (!normalized) return false;
    if (normalized === "/placeholder.png") return false;
    return !normalized.includes("default.png");
};

const resolvePlaylistTitleFromSessionData = (sessionData: any): string => {
    const playlistTitle = pickString(
        sessionData?.playlist?.title,
        sessionData?.playlist?.name,
        sessionData?.playlist_title,
        sessionData?.playlist_name,
        sessionData?.playlistName
    );
    const sessionTitle = pickString(
        sessionData?.name,
        sessionData?.session_name,
        sessionData?.sessionName
    );

    if (playlistTitle && !looksLikeIdentifier(playlistTitle)) return playlistTitle;
    if (sessionTitle && !looksLikeIdentifier(sessionTitle)) return sessionTitle;
    return playlistTitle || sessionTitle || "Untitled Session";
};

const resolvePlaylistImageFromSessionData = (sessionData: any): string => {
    const candidates = [
        sessionData?.playlist?.logo_url,
        sessionData?.playlist?.thumb_url,
        sessionData?.playlist?.image,
        sessionData?.playlist?.cover_image,
        sessionData?.playlist?.artwork_url,
        sessionData?.playlist_logo_url,
        sessionData?.playlist_thumb_url,
        sessionData?.playlist_image,
        sessionData?.playlist_cover_image,
        sessionData?.playlist_artwork_url,
        sessionData?.image,
        sessionData?.cover_image,
        sessionData?.artwork_url,
        sessionData?.songs?.[0]?.image,
    ]
        .map(normalizeAssetUrl)
        .filter(Boolean);

    const playlistKey = pickString(
        sessionData?.playlist?.id,
        sessionData?.playlist_id,
        sessionData?.playlistId,
        sessionData?.session_id,
        sessionData?.sessionId,
        sessionData?.id
    );

    const preferred =
        candidates.find((candidate) => isUsablePlaylistImage(candidate)) ||
        candidates[0] ||
        "";

    return resolvePlaylistImage(preferred, false, playlistKey);
};

const unwrapPlaylistPayload = (payload: unknown): Record<string, unknown> | null => {
    if (!payload || typeof payload !== "object") return null;

    const source = payload as Record<string, unknown>;
    const candidates: Array<Record<string, unknown>> = [source];

    if (source.data && typeof source.data === "object") {
        candidates.push(source.data as Record<string, unknown>);
    }

    if (source.playlist && typeof source.playlist === "object") {
        candidates.push(source.playlist as Record<string, unknown>);
    }

    return candidates.find((candidate) =>
        Boolean(candidate.id ?? candidate.playlist_id ?? candidate.session_id)
    ) || null;
};

const getStoredAuthToken = (): string =>
    ["authToken", "auth_token", "jwt_token", "access_token", "token"]
        .map((key) => localStorage.getItem(key))
        .find(Boolean) || "";

const resolveSessionAvatarHeader = (...candidates: unknown[]): string => {
    const avatar = pickString(...candidates);
    if (!avatar) return "qit-avatar:auto";
    if (avatar.startsWith("http://") || avatar.startsWith("https://")) return avatar;
    if (avatar.startsWith("qit-avatar:")) return avatar;
    return "qit-avatar:auto";
};

const parseStoredJson = (key: string): any => {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const getSessionIdentityHeaders = (token?: string): Record<string, string> => {
    const headers: Record<string, string> = {};
    const guestIdPattern = /^guest_[a-z0-9][a-z0-9_-]{2,63}$/i;
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const storedGuest = parseStoredJson("guestUser");
    const storedUser = parseStoredJson("user");

    const rawGuestId = token
        ? ""
        : pickString(
            storedGuest?.id,
            storedGuest?.user_id,
            storedGuest?.userId
        );
    const guestId = guestIdPattern.test(rawGuestId) ? rawGuestId : "";
    const guestName = token
        ? ""
        : pickString(
            storedGuest?.name,
            storedGuest?.username,
            storedGuest?.user_name,
            storedGuest?.userName,
            storedUser?.name,
            storedUser?.username,
            storedUser?.user_name
        );
    const avatar = resolveSessionAvatarHeader(
        storedUser?.avatar,
        storedUser?.avatar_url,
        storedUser?.avatarUrl,
        storedGuest?.avatar,
        storedGuest?.avatar_url,
        storedGuest?.avatarUrl
    );

    if (!token && guestId) headers["X-Guest-Id"] = guestId;
    if (!token && guestName) headers["X-Guest-Name"] = guestName;
    if (avatar) headers["X-User-Avatar"] = avatar;
    return headers;
};

const enrichSessionDataWithPlaylistTitle = async (
    sessionData: any,
    token?: string
): Promise<any> => {
    if (!sessionData || typeof sessionData !== "object") return sessionData;

    const hasPlaylistTitle = Boolean(
        pickString(
            sessionData?.playlist?.title,
            sessionData?.playlist?.name,
            sessionData?.playlist_title,
            sessionData?.playlist_name,
            sessionData?.playlistName
        )
    );

    const playlistId = pickString(
        sessionData?.playlist_id,
        sessionData?.playlist?.id
    );

    const hasPlaylistImage = [
        sessionData?.playlist?.logo_url,
        sessionData?.playlist?.thumb_url,
        sessionData?.playlist?.image,
        sessionData?.playlist?.cover_image,
        sessionData?.playlist?.artwork_url,
        sessionData?.playlist_logo_url,
        sessionData?.playlist_thumb_url,
        sessionData?.playlist_image,
        sessionData?.playlist_cover_image,
        sessionData?.playlist_artwork_url,
        sessionData?.image,
        sessionData?.cover_image,
        sessionData?.artwork_url,
        sessionData?.songs?.[0]?.image,
    ]
        .map(normalizeAssetUrl)
        .some((candidate) => isUsablePlaylistImage(candidate));

    const hasPlaylistLikes = Number.isFinite(
        Number(
            sessionData?.likes ??
            sessionData?.like_count ??
            sessionData?.likes_count ??
            sessionData?.likeCount ??
            sessionData?.playlist?.likes
        )
    );

    const hasPlaylistComments = Number.isFinite(
        Number(
            sessionData?.comments ??
            sessionData?.comment_count ??
            sessionData?.comments_count ??
            sessionData?.commentCount ??
            sessionData?.playlist?.comments ??
            sessionData?.playlist?.comment_count ??
            sessionData?.playlist?.comments_count ??
            sessionData?.playlist?.commentCount
        )
    );

    const hasPlaylistRatings = Number.isFinite(
        Number(
            sessionData?.ratings ??
            sessionData?.rating ??
            sessionData?.playlist?.ratings ??
            sessionData?.playlist?.rating
        )
    );

    if ((hasPlaylistTitle && hasPlaylistImage && hasPlaylistLikes && hasPlaylistComments && hasPlaylistRatings) || !playlistId) {
        return sessionData;
    }

    try {
        const headers: Record<string, string> = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const playlistRes = await fetch(`/api/v1/playlists/${playlistId}`, {
            headers,
        });
        if (!playlistRes.ok) return sessionData;

        const playlistRaw = await playlistRes.json();
        const playlistData = unwrapPlaylistPayload(playlistRaw) || {};
        const fetchedTitle = pickString(
            playlistData?.title,
            playlistData?.name,
            playlistData?.playlist_title,
            playlistData?.playlistName
        );
        const fetchedLogo = normalizeAssetUrl(
            playlistData?.logo_url || playlistData?.image || playlistData?.cover_image
        );
        const fetchedThumb = normalizeAssetUrl(
            playlistData?.thumb_url || playlistData?.logo_url || playlistData?.image
        );
        const fetchedImage = fetchedLogo || fetchedThumb;
        const fetchedLikes = Number(
            playlistData?.likes ??
            playlistData?.like_count ??
            playlistData?.likes_count ??
            playlistData?.likeCount ??
            0
        );
        const fetchedComments = Number(
            playlistData?.comments ??
            playlistData?.comment_count ??
            playlistData?.comments_count ??
            playlistData?.commentCount ??
            0
        );
        const fetchedRatings = Number(
            playlistData?.ratings ??
            playlistData?.rating ??
            0
        );

        if (
            !fetchedTitle &&
            !fetchedImage &&
            !Number.isFinite(fetchedLikes) &&
            !Number.isFinite(fetchedComments) &&
            !Number.isFinite(fetchedRatings)
        ) {
            return sessionData;
        }

        return {
            ...sessionData,
            playlist_title: sessionData?.playlist_title || fetchedTitle,
            playlist_name: sessionData?.playlist_name || fetchedTitle,
            playlist_image: sessionData?.playlist_image || fetchedImage,
            playlist_logo_url: sessionData?.playlist_logo_url || fetchedLogo,
            playlist_thumb_url: sessionData?.playlist_thumb_url || fetchedThumb,
            likes:
                sessionData?.likes ??
                sessionData?.like_count ??
                sessionData?.likes_count ??
                sessionData?.likeCount ??
                (Number.isFinite(fetchedLikes) ? fetchedLikes : 0),
            comments:
                sessionData?.comments ??
                sessionData?.comment_count ??
                sessionData?.comments_count ??
                sessionData?.commentCount ??
                (Number.isFinite(fetchedComments) ? fetchedComments : 0),
            comment_count:
                sessionData?.comment_count ??
                sessionData?.comments_count ??
                sessionData?.commentCount ??
                sessionData?.comments ??
                (Number.isFinite(fetchedComments) ? fetchedComments : 0),
            comments_count:
                sessionData?.comments_count ??
                sessionData?.comment_count ??
                sessionData?.commentCount ??
                sessionData?.comments ??
                (Number.isFinite(fetchedComments) ? fetchedComments : 0),
            commentCount:
                sessionData?.commentCount ??
                sessionData?.comment_count ??
                sessionData?.comments_count ??
                sessionData?.comments ??
                (Number.isFinite(fetchedComments) ? fetchedComments : 0),
            ratings:
                sessionData?.ratings ??
                sessionData?.rating ??
                (Number.isFinite(fetchedRatings) ? fetchedRatings : 0),
            rating:
                sessionData?.rating ??
                sessionData?.ratings ??
                (Number.isFinite(fetchedRatings) ? fetchedRatings : 0),
            playlist: {
                ...(sessionData?.playlist || {}),
                id: sessionData?.playlist?.id || playlistId,
                title: fetchedTitle || sessionData?.playlist?.title || sessionData?.playlist?.name,
                name: fetchedTitle || sessionData?.playlist?.name || sessionData?.playlist?.title,
                image: sessionData?.playlist?.image || fetchedImage,
                logo_url: sessionData?.playlist?.logo_url || fetchedLogo,
                thumb_url: sessionData?.playlist?.thumb_url || fetchedThumb,
                likes:
                    sessionData?.playlist?.likes ??
                    sessionData?.likes ??
                    sessionData?.like_count ??
                    sessionData?.likes_count ??
                    sessionData?.likeCount ??
                    (Number.isFinite(fetchedLikes) ? fetchedLikes : 0),
                comments:
                    sessionData?.playlist?.comments ??
                    sessionData?.comments ??
                    sessionData?.comment_count ??
                    sessionData?.comments_count ??
                    sessionData?.commentCount ??
                    (Number.isFinite(fetchedComments) ? fetchedComments : 0),
                comment_count:
                    sessionData?.playlist?.comment_count ??
                    sessionData?.comment_count ??
                    sessionData?.comments_count ??
                    sessionData?.commentCount ??
                    sessionData?.comments ??
                    (Number.isFinite(fetchedComments) ? fetchedComments : 0),
                comments_count:
                    sessionData?.playlist?.comments_count ??
                    sessionData?.comments_count ??
                    sessionData?.comment_count ??
                    sessionData?.commentCount ??
                    sessionData?.comments ??
                    (Number.isFinite(fetchedComments) ? fetchedComments : 0),
                commentCount:
                    sessionData?.playlist?.commentCount ??
                    sessionData?.commentCount ??
                    sessionData?.comment_count ??
                    sessionData?.comments_count ??
                    sessionData?.comments ??
                    (Number.isFinite(fetchedComments) ? fetchedComments : 0),
                ratings:
                    sessionData?.playlist?.ratings ??
                    sessionData?.playlist?.rating ??
                    sessionData?.ratings ??
                    sessionData?.rating ??
                    (Number.isFinite(fetchedRatings) ? fetchedRatings : 0),
                rating:
                    sessionData?.playlist?.rating ??
                    sessionData?.playlist?.ratings ??
                    sessionData?.rating ??
                    sessionData?.ratings ??
                    (Number.isFinite(fetchedRatings) ? fetchedRatings : 0),
            },
        };
    } catch {
        return sessionData;
    }
};

const createPlaylistFromSessionData = (sessionData: any): Playlist => ({
    id: sessionData.playlist_id || sessionData?.playlist?.id || `session-${sessionData.session_id}`,
    title: resolvePlaylistTitleFromSessionData(sessionData),
    creator:
        pickString(
            sessionData?.host_name,
            sessionData?.hostName,
            sessionData?.host_username,
            sessionData?.hostUsername,
            sessionData?.host?.name,
            sessionData?.host?.username
        ) || "Unknown",
    description: "",
    location: "US",
    access: "Public",
    genre: ["Other"],
    image: resolvePlaylistImageFromSessionData(sessionData),
    venueName: sessionData.name,
    venue_id: "",
    likes:
        Number(
            sessionData?.likes ??
            sessionData?.like_count ??
            sessionData?.likes_count ??
            sessionData?.likeCount ??
            sessionData?.playlist?.likes ??
            0
        ) || 0,
    like_count:
        Number(
            sessionData?.like_count ??
            sessionData?.likes_count ??
            sessionData?.likes ??
            sessionData?.likeCount ??
            sessionData?.playlist?.like_count ??
            sessionData?.playlist?.likes_count ??
            sessionData?.playlist?.likes ??
            0
        ) || 0,
    likes_count:
        Number(
            sessionData?.likes_count ??
            sessionData?.like_count ??
            sessionData?.likes ??
            sessionData?.likeCount ??
            sessionData?.playlist?.likes_count ??
            sessionData?.playlist?.like_count ??
            sessionData?.playlist?.likes ??
            0
        ) || 0,
    liked_by_user: Boolean(
        sessionData?.liked_by_user ??
        sessionData?.likedByUser ??
        sessionData?.playlist?.liked_by_user ??
        sessionData?.playlist?.likedByUser ??
        false
    ),
    comments:
        Number(
            sessionData?.comments ??
            sessionData?.comment_count ??
            sessionData?.comments_count ??
            sessionData?.commentCount ??
            sessionData?.playlist?.comments ??
            sessionData?.playlist?.comment_count ??
            sessionData?.playlist?.comments_count ??
            sessionData?.playlist?.commentCount ??
            0
        ) || 0,
    comment_count:
        Number(
            sessionData?.comment_count ??
            sessionData?.comments_count ??
            sessionData?.commentCount ??
            sessionData?.comments ??
            sessionData?.playlist?.comment_count ??
            sessionData?.playlist?.comments_count ??
            sessionData?.playlist?.commentCount ??
            sessionData?.playlist?.comments ??
            0
        ) || 0,
    comments_count:
        Number(
            sessionData?.comments_count ??
            sessionData?.comment_count ??
            sessionData?.commentCount ??
            sessionData?.comments ??
            sessionData?.playlist?.comments_count ??
            sessionData?.playlist?.comment_count ??
            sessionData?.playlist?.commentCount ??
            sessionData?.playlist?.comments ??
            0
        ) || 0,
    commentCount:
        Number(
            sessionData?.commentCount ??
            sessionData?.comment_count ??
            sessionData?.comments_count ??
            sessionData?.comments ??
            sessionData?.playlist?.commentCount ??
            sessionData?.playlist?.comment_count ??
            sessionData?.playlist?.comments_count ??
            sessionData?.playlist?.comments ??
            0
        ) || 0,
    ratings:
        Number(
            sessionData?.ratings ??
            sessionData?.rating ??
            sessionData?.playlist?.ratings ??
            sessionData?.playlist?.rating ??
            0
        ) || 0,
    songs: sessionData.songs?.map(createSongFromData) || [],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
});

const resolveSessionIsPlaying = (
    sessionData: any,
    currentSong: Song | null
): boolean => {
    if (!currentSong) return false;

    const explicitIsPlaying = [
        sessionData?.is_playing,
        sessionData?.isPlaying,
        sessionData?.playing,
    ].find((value) => typeof value === "boolean");

    if (typeof explicitIsPlaying === "boolean") {
        return explicitIsPlaying;
    }

    return true;
};

const extractSessionSettingsFromData = (sessionData: any): unknown => {
    return (
        sessionData?.settings ??
        sessionData?.session_settings ??
        sessionData?.sessionSettings ??
        sessionData?.playback_settings ??
        sessionData?.playbackSettings ??
        {}
    );
};

const getCurrentSessionUserIds = (currentUserId?: string): string[] => {
    const storedGuest = parseStoredJson("guestUser");
    const storedUser = parseStoredJson("user");
    return [
        currentUserId,
        storedUser?.id,
        storedUser?.user_id,
        storedUser?.userId,
        storedGuest?.id,
        storedGuest?.user_id,
        storedGuest?.userId,
    ]
        .map((value) => String(value || "").trim())
        .filter(Boolean);
};

const isCurrentUserBlockedFromSessionData = (sessionData: any, currentUserId?: string): boolean => {
    const settings = extractSessionSettingsFromData(sessionData) as Record<string, unknown>;
    const blockedUsers = Array.isArray(settings?.blocked_users)
        ? settings.blocked_users as Array<Record<string, unknown>>
        : Array.isArray((settings as any)?.blockedUsers)
            ? (settings as any).blockedUsers as Array<Record<string, unknown>>
            : [];
    if (blockedUsers.length === 0) return false;

    const currentUserIds = new Set(getCurrentSessionUserIds(currentUserId));
    if (currentUserIds.size === 0) return false;

    return blockedUsers.some((entry) => {
        const blockedUserId = pickString(entry?.user_id, entry?.userId, entry?.id);
        return Boolean(blockedUserId && currentUserIds.has(blockedUserId));
    });
};

const createSessionInfoFromData = (sessionData: any, currentUserId?: string): SessionInfo => {
    const explicitIsHost = typeof sessionData?.is_host === "boolean" ? sessionData.is_host : undefined;
    const explicitUserInSession = typeof sessionData?.user_in_session === "boolean" ? sessionData.user_in_session : undefined;
    const normalizedCurrentUserId = currentUserId ? String(currentUserId) : "";
    const normalizedUsers = Array.isArray(sessionData?.users)
        ? sessionData.users
        : Array.isArray(sessionData?.participants)
            ? sessionData.participants
            : [];
    const hostCandidateFromUsers = normalizedUsers.find((user: any) =>
        Boolean(user?.is_host || user?.isHost)
    );
    const resolvedHostId = pickString(
        sessionData?.host_id,
        sessionData?.hostId,
        sessionData?.host?.id,
        hostCandidateFromUsers?.user_id,
        hostCandidateFromUsers?.id
    );
    const resolvedHostName = pickString(
        sessionData?.host_name,
        sessionData?.hostName,
        sessionData?.host_username,
        sessionData?.hostUsername,
        sessionData?.host?.name,
        sessionData?.host?.username,
        hostCandidateFromUsers?.username,
        hostCandidateFromUsers?.name
    );
    const normalizedHostId = resolvedHostId ? String(resolvedHostId) : "";
    const normalizedSettings = normalizeSessionSettings(extractSessionSettingsFromData(sessionData));
    const resolvedIsHost = explicitIsHost === true ? true : (normalizedCurrentUserId ? normalizedHostId === normalizedCurrentUserId : false);
    const participantCountCandidate = Number(
        sessionData?.user_count ??
        sessionData?.joined_users ??
        sessionData?.joinedUsers ??
        sessionData?.participants_count ??
        sessionData?.participantsCount ??
        normalizedUsers.length
    );
    const songCountCandidate = Number(
        sessionData?.song_count ??
        sessionData?.songs_count ??
        sessionData?.songCount ??
        sessionData?.songs?.length ??
        sessionData?.playlist?.songs?.length ??
        0
    );
    const fallbackCanEdit =
        resolvedIsHost ||
        (Boolean(normalizedSettings.allow_guest_add) && !normalizedSettings.require_approval);
    const fallbackCanAddToQueue =
        resolvedIsHost ||
        (Boolean(explicitUserInSession ?? true) &&
            Boolean(normalizedSettings.allow_guest_add) &&
            !normalizedSettings.require_approval);
    const fallbackCanSuggest =
        !resolvedIsHost &&
        Boolean(explicitUserInSession ?? true) &&
        (!normalizedSettings.allow_guest_add || normalizedSettings.require_approval);

    return {
        id: pickString(sessionData?.session_id, sessionData?.id),
        name: resolvePlaylistTitleFromSessionData(sessionData) || "Live Session",
        hostName: resolvedHostName || "Host",
        hostId: resolvedHostId,
        playlistId: pickString(sessionData?.playlist_id, sessionData?.playlist?.id),
        users: normalizedUsers,
        is_host: resolvedIsHost,
        can_edit: typeof sessionData?.can_edit === "boolean" ? sessionData.can_edit : fallbackCanEdit,
        can_add_to_queue:
            typeof sessionData?.can_add_to_queue === "boolean"
                ? sessionData.can_add_to_queue
                : typeof sessionData?.canAddToQueue === "boolean"
                    ? sessionData.canAddToQueue
                    : fallbackCanAddToQueue,
        can_suggest:
            typeof sessionData?.can_suggest === "boolean"
                ? sessionData.can_suggest
                : typeof sessionData?.canSuggest === "boolean"
                    ? sessionData.canSuggest
                    : fallbackCanSuggest,
        user_in_session: explicitUserInSession,
        songCount: Number.isFinite(songCountCandidate) ? Math.max(0, Math.floor(songCountCandidate)) : 0,
        participants: Number.isFinite(participantCountCandidate) ? Math.max(1, Math.floor(participantCountCandidate)) : 1,
        settings: normalizedSettings,
    };
};

const LEGACY_SESSION_STORAGE_KEYS = {
    currentPlaylist: "currentPlaylist",
    sessionId: "current_session_id",
    sessionInfo: "current_session_info",
} as const;

const getScopedSessionStorageKeys = (userId?: string | null) => {
    const scope = userId ? `user:${userId}` : "guest";
    return {
        currentPlaylist: `session:${scope}:currentPlaylist`,
        sessionId: `session:${scope}:current_session_id`,
        sessionInfo: `session:${scope}:current_session_info`,
    };
};

const SESSION_STATUS_REFRESH_INTERVAL_MS = 2500;

// ---------- Provider ----------
export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(sessionReducer, {
        currentPlaylist: null,
        sessionId: null,
        currentSong: null,
        isPlaying: false,
        sessionActive: false,
        sessionInfo: null,
    });
    const { user, loading: userLoading } = useUser();
    const { setAllowGuestAdd, setShuffleOnSessionStart, setMinVote } = usePlaybackSettings();
    const { pusher } = useSocket();
    const [channel, setChannel] = useState<any>(null);
    const sessionBootstrapRetryAttemptsRef = React.useRef<Record<string, number>>({});
    const sessionBootstrapRetryTimersRef = React.useRef<Record<string, number>>({});
    const restoredSessionCacheKeysRef = React.useRef<Set<string>>(new Set());
    const loadFullSessionInfoRef = React.useRef<(sessionId: string) => Promise<void>>(async () => { });
    const queueRevisionRef = React.useRef(0);
    const queueRevisionSessionRef = React.useRef<string | null>(null);
    const latestPlaybackUpdatedAtBySessionRef = React.useRef<Record<string, string>>({});
    const latestSessionIsPlayingRef = React.useRef(state.isPlaying);
    const latestSessionSongCountRef = React.useRef(state.currentPlaylist?.songs?.length || 0);
    const latestCurrentSongRef = React.useRef(state.currentSong);
    const latestSessionInfoRef = React.useRef(state.sessionInfo);
    latestSessionIsPlayingRef.current = state.isPlaying;
    latestSessionSongCountRef.current = state.currentPlaylist?.songs?.length || 0;
    latestCurrentSongRef.current = state.currentSong;
    latestSessionInfoRef.current = state.sessionInfo;
    const scopedStorageKeys = useMemo(
        () => getScopedSessionStorageKeys(user?.id ?? null),
        [user?.id]
    );

    useEffect(() => {
        if (queueRevisionSessionRef.current !== state.sessionId) {
            queueRevisionSessionRef.current = state.sessionId;
            queueRevisionRef.current = 0;
        }

        const serverRevision = Number(state.sessionInfo?.settings?.queue_revision);
        if (Number.isInteger(serverRevision) && serverRevision >= 0) {
            queueRevisionRef.current = Math.max(queueRevisionRef.current, serverRevision);
        }
    }, [state.sessionId, state.sessionInfo?.settings?.queue_revision]);

    const clearSessionBootstrapRetry = useCallback((sessionId: string) => {
        const normalizedSessionId = String(sessionId || "").trim();
        if (!normalizedSessionId) return;

        const existingTimerId = sessionBootstrapRetryTimersRef.current[normalizedSessionId];
        if (existingTimerId) {
            window.clearTimeout(existingTimerId);
            delete sessionBootstrapRetryTimersRef.current[normalizedSessionId];
        }
        delete sessionBootstrapRetryAttemptsRef.current[normalizedSessionId];
    }, []);

    const scheduleSessionBootstrapRetry = useCallback((sessionId: string, reason: string) => {
        const normalizedSessionId = String(sessionId || "").trim();
        if (!normalizedSessionId || typeof window === "undefined") return;

        const previousAttempt =
            sessionBootstrapRetryAttemptsRef.current[normalizedSessionId] || 0;
        const { attempt, delayMs } =
            getSessionBootstrapRetryPlan(previousAttempt);
        sessionBootstrapRetryAttemptsRef.current[normalizedSessionId] = attempt;
        const existingTimerId = sessionBootstrapRetryTimersRef.current[normalizedSessionId];
        if (existingTimerId) {
            window.clearTimeout(existingTimerId);
        }

        if (attempt === 1) {
            logger.sampledWarn("session.bootstrap.deferred", {
                session_id: normalizedSessionId,
                reason,
                retry_in_ms: delayMs,
            });
        }

        sessionBootstrapRetryTimersRef.current[normalizedSessionId] = window.setTimeout(() => {
            delete sessionBootstrapRetryTimersRef.current[normalizedSessionId];
            void loadFullSessionInfoRef.current(normalizedSessionId);
        }, delayMs);
    }, []);

    // Sync with CurrentPlaylistContext
    useEffect(() => {
        const handleCurrentPlaylistUpdate = (event: Event) => {
            const customEvent = event as CustomEvent;
            const { playlist } = customEvent.detail || {};
            if (!playlist || !playlist.id || !state.currentPlaylist) return;

            if (playlist.id !== state.currentPlaylist.id) return;

            dispatch({
                type: "SET_FULL_SESSION",
                payload: {
                    playlist: {
                        ...state.currentPlaylist,
                        ...playlist,
                        songs: playlist.songs || state.currentPlaylist.songs
                    },
                    sessionInfo: state.sessionInfo!,
                    sessionId: state.sessionId!
                }
            });

            localStorage.setItem(scopedStorageKeys.currentPlaylist, JSON.stringify(playlist));
        };

        window.addEventListener("currentPlaylistUpdated", handleCurrentPlaylistUpdate);
        return () => {
            window.removeEventListener("currentPlaylistUpdated", handleCurrentPlaylistUpdate);
        };
    }, [scopedStorageKeys.currentPlaylist, state.currentPlaylist, state.sessionInfo, state.sessionId]);

    // Sync helpers
    const syncWithCurrentPlaylistContext = useCallback((
        playlist: Playlist,
        sessionId: string,
        sessionInfo: SessionInfo
    ) => {
        localStorage.setItem(scopedStorageKeys.currentPlaylist, JSON.stringify(playlist));
        localStorage.setItem(scopedStorageKeys.sessionId, sessionId);
        localStorage.setItem(scopedStorageKeys.sessionInfo, JSON.stringify(sessionInfo));
        window.dispatchEvent(new CustomEvent("sessionPlaylistSync", {
            detail: { playlist, sessionId, sessionInfo }
        }));
        window.dispatchEvent(new Event("storage"));
    }, [scopedStorageKeys.currentPlaylist, scopedStorageKeys.sessionId, scopedStorageKeys.sessionInfo]);

    const clearSyncWithCurrentPlaylistContext = useCallback(() => {
        localStorage.removeItem(scopedStorageKeys.currentPlaylist);
        localStorage.removeItem(scopedStorageKeys.sessionId);
        localStorage.removeItem(scopedStorageKeys.sessionInfo);

        // Cleanup old unscoped keys so prior account data cannot leak.
        localStorage.removeItem(LEGACY_SESSION_STORAGE_KEYS.currentPlaylist);
        localStorage.removeItem(LEGACY_SESSION_STORAGE_KEYS.sessionId);
        localStorage.removeItem(LEGACY_SESSION_STORAGE_KEYS.sessionInfo);
        window.dispatchEvent(new Event("storage"));
    }, [scopedStorageKeys.currentPlaylist, scopedStorageKeys.sessionId, scopedStorageKeys.sessionInfo]);

    const restoreSessionFromScopedCache = useCallback((sessionId: string): boolean => {
        const normalizedSessionId = String(sessionId || "").trim();
        if (!normalizedSessionId || typeof window === "undefined") return false;

        const cacheKey = `${scopedStorageKeys.sessionId}:${normalizedSessionId}`;
        if (restoredSessionCacheKeysRef.current.has(cacheKey)) return false;

        const snapshot = readCachedSessionSnapshot({
            expectedSessionId: normalizedSessionId,
            storedSessionId: localStorage.getItem(scopedStorageKeys.sessionId),
            serializedPlaylist: localStorage.getItem(scopedStorageKeys.currentPlaylist),
            serializedSessionInfo: localStorage.getItem(scopedStorageKeys.sessionInfo),
        });
        if (!snapshot) return false;

        restoredSessionCacheKeysRef.current.add(cacheKey);
        const sessionInfo = snapshot.sessionInfo as unknown as SessionInfo;
        dispatch({
            type: "RESTORE_CACHED_SESSION",
            payload: {
                playlist: snapshot.playlist,
                sessionInfo,
                sessionId: snapshot.sessionId,
            },
        });
        window.dispatchEvent(new CustomEvent("sessionPlaylistSync", {
            detail: {
                playlist: snapshot.playlist,
                sessionId: snapshot.sessionId,
                sessionInfo,
            },
        }));
        return true;
    }, [
        scopedStorageKeys.currentPlaylist,
        scopedStorageKeys.sessionId,
        scopedStorageKeys.sessionInfo,
    ]);

    const finalizeEndedSession = useCallback((options?: {
        sessionId?: string | null;
        sessionTitle?: string | null;
        actor?: string | null;
        trackActivity?: boolean;
    }) => {
        const endedSessionId = String(options?.sessionId || state.sessionId || "").trim();
        const endedSessionTitle =
            String(
                options?.sessionTitle ||
                state.currentPlaylist?.title ||
                state.currentPlaylist?.name ||
                state.sessionInfo?.name ||
                "Live Session"
            ).trim() || "Live Session";

        if (endedSessionId) {
            markRecentlyJoinedPlaylistOffline(endedSessionId);
            if (options?.trackActivity) {
                addSessionActivity({
                    action: "ended",
                    sessionId: endedSessionId,
                    playlistId: state.sessionInfo?.playlistId || state.currentPlaylist?.id || undefined,
                    sessionTitle: endedSessionTitle,
                    actor: options?.actor || state.sessionInfo?.hostName || "Host",
                });
            }
        }

        dispatch({ type: "CLEAR_SESSION" });
        clearSyncWithCurrentPlaylistContext();

        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("qit-session-ended", {
                    detail: {
                        sessionId: endedSessionId,
                        sessionTitle: endedSessionTitle,
                    },
                })
            );

            scheduleSessionEndPageRefresh();
        }
    }, [
        clearSyncWithCurrentPlaylistContext,
        state.currentPlaylist?.id,
        state.currentPlaylist?.name,
        state.currentPlaylist?.title,
        state.sessionId,
        state.sessionInfo?.hostName,
        state.sessionInfo?.name,
        state.sessionInfo?.playlistId,
    ]);

    // Load session info (SIMPLIFIED - uses session data directly)
    const loadFullSessionInfo = useCallback(async (sessionId: string) => {
        const normalizedSessionId = String(sessionId || "").trim();
        if (!normalizedSessionId) {
            return;
        }
        if (wasSessionRecentlyLeft(normalizedSessionId)) {
            clearSessionBootstrapRetry(normalizedSessionId);
            clearSyncWithCurrentPlaylistContext();
            return;
        }

        try {
            const token = getStoredAuthToken();
            const headers = getSessionIdentityHeaders(token);

            const sessionRes = await fetch(`/api/v1/sessions/${normalizedSessionId}`, {
                headers,
            });

            if (!sessionRes.ok) {
                if (sessionRes.status === 404 || sessionRes.status === 410) {
                    clearSessionBootstrapRetry(normalizedSessionId);
                    finalizeEndedSession({
                        sessionId: normalizedSessionId,
                        trackActivity: false,
                    });
                } else if (sessionRes.status === 401) {
                    clearSessionBootstrapRetry(normalizedSessionId);
                    dispatch({ type: "CLEAR_SESSION" });
                    clearSyncWithCurrentPlaylistContext();
                } else if (sessionRes.status === 403) {
                    clearSessionBootstrapRetry(normalizedSessionId);
                } else {
                    scheduleSessionBootstrapRetry(
                        normalizedSessionId,
                        `http-${sessionRes.status}`
                    );
                }
                return;
            }

            const sessionData = await sessionRes.json();
            if (isCurrentUserBlockedFromSessionData(sessionData, user?.id)) {
                markSessionRecentlyLeft(normalizedSessionId);
                clearSessionBootstrapRetry(normalizedSessionId);
                clearSyncWithCurrentPlaylistContext();
                return;
            }

            if (wasSessionRecentlyLeft(normalizedSessionId)) {
                clearSessionBootstrapRetry(normalizedSessionId);
                clearSyncWithCurrentPlaylistContext();
                return;
            }

            const normalizedSessionData = await enrichSessionDataWithPlaylistTitle(sessionData, token);
            const sessionKeyForTimestamp = String(normalizedSessionId || state.sessionId || "").trim();
            const sessionUpdatedAt = typeof normalizedSessionData?.updated_at === "string"
                ? normalizedSessionData.updated_at
                : null;
            if (sessionKeyForTimestamp && sessionUpdatedAt) {
                latestPlaybackUpdatedAtBySessionRef.current[sessionKeyForTimestamp] = sessionUpdatedAt;
            }
            if (isCurrentUserBlockedFromSessionData(normalizedSessionData, user?.id)) {
                markSessionRecentlyLeft(normalizedSessionId);
                clearSessionBootstrapRetry(normalizedSessionId);
                clearSyncWithCurrentPlaylistContext();
                return;
            }

            if (wasSessionRecentlyLeft(normalizedSessionId)) {
                clearSessionBootstrapRetry(normalizedSessionId);
                clearSyncWithCurrentPlaylistContext();
                return;
            }

            // A reconciliation request can finish after newer realtime events. Do not
            // let that older snapshot overwrite an already-applied queue revision.
            const incomingSettings = extractSessionSettingsFromData(normalizedSessionData) as Record<string, unknown>;
            const incomingQueueRevision = Number(
                normalizedSessionData?.queue_revision ??
                normalizedSessionData?.queueRevision ??
                incomingSettings?.queue_revision ??
                incomingSettings?.queueRevision
            );
            const isCurrentQueueSession = queueRevisionSessionRef.current === normalizedSessionId;
            const hasIncomingQueueRevision =
                Number.isInteger(incomingQueueRevision) && incomingQueueRevision >= 0;
            if (
                isCurrentQueueSession &&
                hasIncomingQueueRevision &&
                incomingQueueRevision < queueRevisionRef.current
            ) {
                console.info("Ignoring stale session queue snapshot", {
                    sessionId: normalizedSessionId,
                    incomingQueueRevision,
                    currentQueueRevision: queueRevisionRef.current,
                });
                clearSessionBootstrapRetry(normalizedSessionId);
                return;
            }
            if (isCurrentQueueSession && hasIncomingQueueRevision) {
                queueRevisionRef.current = Math.max(queueRevisionRef.current, incomingQueueRevision);
            }

            const playlist = createPlaylistFromSessionData(normalizedSessionData);
            const sessionInfo = createSessionInfoFromData(normalizedSessionData, user?.id);
            const currentSong = normalizedSessionData.current_song
                ? createSongFromData(normalizedSessionData.current_song)
                : null;

            dispatch({
                type: "SET_FULL_SESSION",
                payload: {
                    playlist,
                    sessionInfo,
                    sessionId: normalizedSessionData.session_id,
                    currentSong,
                    isPlaying: Boolean(normalizedSessionData.is_playing && currentSong),
                },
            });

            clearSessionBootstrapRetry(normalizedSessionId);
            syncWithCurrentPlaylistContext(playlist, normalizedSessionId, sessionInfo);

        } catch (err) {
            logger.error("session.load_failed", err, { session_id: normalizedSessionId });
            scheduleSessionBootstrapRetry(normalizedSessionId, "network-or-runtime-error");
        }
    }, [
        clearSessionBootstrapRetry,
        clearSyncWithCurrentPlaylistContext,
        finalizeEndedSession,
        scheduleSessionBootstrapRetry,
        syncWithCurrentPlaylistContext,
        user
    ]);

    useEffect(() => {
        loadFullSessionInfoRef.current = loadFullSessionInfo;
    }, [loadFullSessionInfo]);

    useEffect(() => {
        return () => {
            Object.values(sessionBootstrapRetryTimersRef.current).forEach((timerId) => {
                window.clearTimeout(timerId);
            });
            sessionBootstrapRetryTimersRef.current = {};
            sessionBootstrapRetryAttemptsRef.current = {};
        };
    }, []);

    // Refresh only session permissions/settings so UI actions stay in sync
    // even if realtime events are delayed/missed.
    const refreshSessionSettings = useCallback(async (targetSessionId: string) => {
        try {
            const token = getStoredAuthToken();
            const headers = getSessionIdentityHeaders(token);
            const response = await fetch(`/api/v1/sessions/${targetSessionId}`, {
                headers,
            });
            if (!response.ok) {
                if (response.status === 404 || response.status === 410) {
                    console.info("Active session no longer exists; clearing local session state.", {
                        sessionId: targetSessionId,
                        status: response.status,
                    });
                    finalizeEndedSession({
                        sessionId: targetSessionId,
                        trackActivity: false,
                    });
                }
                return;
            }

            const sessionData = await response.json();
            const normalizedSettings = normalizeSessionSettings(
                extractSessionSettingsFromData(sessionData)
            );
            const explicitIsHost =
                typeof sessionData?.is_host === "boolean" ? sessionData.is_host : undefined;
            const explicitUserInSession =
                typeof sessionData?.user_in_session === "boolean"
                    ? sessionData.user_in_session
                    : state.sessionInfo?.user_in_session;
            const fallbackCanEdit = Boolean(
                (explicitIsHost ?? state.sessionInfo?.is_host) ||
                (normalizedSettings.allow_guest_add && !normalizedSettings.require_approval)
            );
            const fallbackCanAddToQueue = Boolean(
                (explicitIsHost ?? state.sessionInfo?.is_host) ||
                (Boolean(explicitUserInSession ?? true) &&
                    normalizedSettings.allow_guest_add &&
                    !normalizedSettings.require_approval)
            );
            const fallbackCanSuggest = Boolean(
                !(explicitIsHost ?? state.sessionInfo?.is_host) &&
                Boolean(explicitUserInSession ?? true) &&
                (!normalizedSettings.allow_guest_add || normalizedSettings.require_approval)
            );
            const nextCanEdit =
                typeof sessionData?.can_edit === "boolean"
                    ? sessionData.can_edit
                    : fallbackCanEdit;
            const nextCanAddToQueue =
                typeof sessionData?.can_add_to_queue === "boolean"
                    ? sessionData.can_add_to_queue
                    : typeof sessionData?.canAddToQueue === "boolean"
                        ? sessionData.canAddToQueue
                        : fallbackCanAddToQueue;
            const nextCanSuggest =
                typeof sessionData?.can_suggest === "boolean"
                    ? sessionData.can_suggest
                    : typeof sessionData?.canSuggest === "boolean"
                        ? sessionData.canSuggest
                        : fallbackCanSuggest;
            const nextHostId = pickString(
                sessionData?.host_id,
                sessionData?.hostId,
                state.sessionInfo?.hostId
            );
            const nextHostName = pickString(
                sessionData?.host_name,
                sessionData?.hostName,
                state.sessionInfo?.hostName
            );

            dispatch({
                type: "UPDATE_SESSION_INFO",
                payload: {
                    settings: normalizedSettings,
                    can_edit: nextCanEdit,
                    can_add_to_queue: nextCanAddToQueue,
                    can_suggest: nextCanSuggest,
                    ...(explicitUserInSession !== undefined ? { user_in_session: explicitUserInSession } : {}),
                    ...(explicitIsHost === true ? { is_host: true } : {}),
                    ...(nextHostId ? { hostId: nextHostId } : {}),
                    ...(nextHostName ? { hostName: nextHostName } : {}),
                },
            });
        } catch (error) {
            console.debug("Failed to refresh session settings:", error);
        }
    }, [
        finalizeEndedSession,
        state.sessionInfo?.is_host,
        state.sessionInfo?.hostId,
        state.sessionInfo?.hostName,
        state.sessionInfo?.user_in_session,
    ]);

    // Clear in-memory session when auth identity changes, then restore from scoped storage.
    useEffect(() => {
        dispatch({ type: "CLEAR_SESSION" });
    }, [user?.id]);

    // Load saved session for the active identity
    useEffect(() => {
        if (userLoading) return;

        const scopedSessionId = localStorage.getItem(scopedStorageKeys.sessionId);
        const savedSessionId = String(
            scopedSessionId ||
            localStorage.getItem(LEGACY_SESSION_STORAGE_KEYS.sessionId) ||
            ""
        ).trim();

        if (savedSessionId) {
            if (wasSessionRecentlyLeft(savedSessionId)) {
                clearSyncWithCurrentPlaylistContext();
                return;
            }
            if (String(scopedSessionId || "").trim() === savedSessionId) {
                restoreSessionFromScopedCache(savedSessionId);
            }
            localStorage.setItem(scopedStorageKeys.sessionId, savedSessionId);
            void loadFullSessionInfo(savedSessionId);
        }
    }, [
        clearSyncWithCurrentPlaylistContext,
        loadFullSessionInfo,
        restoreSessionFromScopedCache,
        scopedStorageKeys.sessionId,
        userLoading,
    ]);

    // Keep persisted session info aligned with runtime updates (for reload continuity).
    useEffect(() => {
        if (!state.sessionId || !state.sessionInfo) return;
        localStorage.setItem(scopedStorageKeys.sessionInfo, JSON.stringify(state.sessionInfo));
    }, [scopedStorageKeys.sessionInfo, state.sessionId, state.sessionInfo]);

    // Fallback settings sync: refresh on focus and every few seconds while in session.
    useEffect(() => {
        if (!state.sessionId || !state.sessionActive) return;

        void refreshSessionSettings(state.sessionId);
        const intervalId = window.setInterval(() => {
            if (state.sessionId) {
                void refreshSessionSettings(state.sessionId);
            }
        }, SESSION_STATUS_REFRESH_INTERVAL_MS);

        const handleFocus = () => {
            if (state.sessionId) {
                void refreshSessionSettings(state.sessionId);
            }
        };

        window.addEventListener("focus", handleFocus);
        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener("focus", handleFocus);
        };
    }, [state.sessionId, state.sessionActive, refreshSessionSettings]);

    // Keep playback settings context aligned with active session settings.
    useEffect(() => {
        const settings = state.sessionInfo?.settings;
        if (!settings) return;

        if (typeof settings.allow_guest_add === "boolean") {
            setAllowGuestAdd(settings.allow_guest_add);
        }
        if (typeof settings.shuffle_on_start === "boolean") {
            setShuffleOnSessionStart(settings.shuffle_on_start);
        }

        const nextMinVote = Number(settings.min_votes);
        if (Number.isFinite(nextMinVote) && nextMinVote >= 0) {
            setMinVote(nextMinVote);
        }
    }, [state.sessionInfo?.settings, setAllowGuestAdd, setShuffleOnSessionStart, setMinVote]);

    // Pusher subscription
    useEffect(() => {
        if (!state.sessionId || !pusher || !state.sessionActive) return;

        const channelName = `session-${state.sessionId}`;
        const sessionChannel = pusher.subscribe(channelName);
        setChannel(sessionChannel);
        let queueReconciliationTimer: number | null = null;
        let queueReconciliationInFlight = false;

        const reconcileQueueOnly = async () => {
            if (!state.sessionId || queueReconciliationInFlight) return;
            queueReconciliationInFlight = true;
            try {
                const token = getStoredAuthToken();
                const response = await fetch(`/api/v1/sessions/${state.sessionId}`, {
                    headers: getSessionIdentityHeaders(token),
                });
                if (!response.ok) return;

                const sessionData = await response.json();
                const playlist = createPlaylistFromSessionData(sessionData);
                dispatch({ type: "SET_SONGS", payload: playlist.songs });

                const revision = Number(
                    sessionData?.queue_revision ??
                    sessionData?.queueRevision ??
                    sessionData?.settings?.queue_revision ??
                    sessionData?.settings?.queueRevision
                );
                if (Number.isInteger(revision) && revision >= 0) {
                    queueRevisionRef.current = Math.max(queueRevisionRef.current, revision);
                }
            } catch (error) {
                logger.sampledWarn("session.queue.reconciliation_failed", { error });
            } finally {
                queueReconciliationInFlight = false;
            }
        };

        const scheduleQueueReconciliation = () => {
            if (queueReconciliationTimer !== null) return;
            queueReconciliationTimer = window.setTimeout(() => {
                queueReconciliationTimer = null;
                void reconcileQueueOnly();
            }, 100);
        };

        const acceptQueueRevision = (
            rawRevision: unknown,
            refreshOnGap = true,
            acceptCurrentRevision = false,
        ): boolean => {
            const revision = Number(rawRevision);
            if (!Number.isInteger(revision) || revision <= 0) return true;
            if (revision < queueRevisionRef.current) return false;
            if (revision === queueRevisionRef.current) return acceptCurrentRevision;
            const hasGap = shouldReconcileQueueRevision(queueRevisionRef.current, revision);
            queueRevisionRef.current = revision;
            if (hasGap && refreshOnGap) {
                scheduleQueueReconciliation();
            }
            return true;
        };

        const handleSongAdded = (data: any) => {
            if (!acceptQueueRevision(data?.queue_revision)) return;
            const song = createSongFromData(data);
            dispatch({ type: "ADD_SONG", payload: song });
            window.dispatchEvent(new CustomEvent("qit-session-song-visual-added", {
                detail: { songId: String(song.id || "").trim() },
            }));
        };

        const handleVoteUpdated = (data: any) => {
            const emittedAtMs = Number(data?.emitted_at_ms);
            console.info("Vote realtime latency", {
                requestId: data?.client_request_id || null,
                transportMs: Number.isFinite(emittedAtMs)
                    ? Math.max(0, Date.now() - emittedAtMs)
                    : null,
                queueRevision: data?.queue_revision ?? null,
            });

            const songId = String(data?.song_id || data?.id || "").trim();
            const position = Number(data?.position);
            if (songId && Number.isInteger(position) && position >= 0) {
                if (!acceptQueueRevision(data?.queue_revision)) return;
                dispatch({
                    type: "APPLY_COMPACT_VOTE",
                    payload: { songId, votes: Number(data?.votes || 0), position },
                });
            }
            else if (data.reordered_songs && Array.isArray(data.reordered_songs)) {
                // Update ALL songs with new votes and order
                dispatch({
                    type: "UPDATE_ALL_SONGS",
                    payload: data.reordered_songs
                });
            }
            else if (data.song_id || data.id) {
                // Fallback: update single song
                dispatch({
                    type: "UPDATE_SONG_VOTES",
                    payload: {
                        songId: data.song_id || data.id,
                        votes: data.votes || 0
                    }
                });
            } else {
                logger.sampledWarn("session.vote.missing_song", { has_payload: Boolean(data) });
            }
        };

        const handleSongPlayed = (data: any) => {
            if (!data) return;
            if (data?.reason === "mix_interrupted_by_vote") {
                if (latestSessionInfoRef.current?.settings?.play_all_songs_by_votes) return;
                // Preserve intentional mix interruption, but reject this event
                // for a normal active song even if a stale/misclassified server
                // instance emits it.
                if (!isMixDuration(latestCurrentSongRef.current?.duration)) return;
                if (typeof window !== "undefined") {
                    window.dispatchEvent(
                        new CustomEvent<SessionTransitionRequestDetail>(
                            SESSION_TRANSITION_REQUEST_EVENT,
                            {
                                detail: {
                                    reason: "mix_interrupted_by_vote",
                                    fromSongId: String(latestCurrentSongRef.current?.id || "").trim() || undefined,
                                    toSongId: String(data?.song_id || data?.id || "").trim() || undefined,
                                },
                            }
                        )
                    );
                }
            }
            const song = createSongFromData(data);
            dispatch({ type: "SET_CURRENT_SONG", payload: song });
            dispatch({ type: "SET_IS_PLAYING", payload: true });
        };

        const handlePlaybackState = (data: any) => {
            if (!data) return;

            const nextUpdatedAt = typeof data?.updated_at === "string" ? data.updated_at : null;
            const sessionKey = String(data?.session_id || state.sessionId || "").trim();
            if (nextUpdatedAt && sessionKey) {
                const existing = latestPlaybackUpdatedAtBySessionRef.current[sessionKey];
                if (existing && nextUpdatedAt < existing) {
                    return;
                }
                latestPlaybackUpdatedAtBySessionRef.current[sessionKey] = nextUpdatedAt;
            }

            const nextPlayingRaw =
                typeof data?.is_playing === "boolean"
                    ? data.is_playing
                    : typeof data?.isPlaying === "boolean"
                        ? data.isPlaying
                        : undefined;

            const nextSongData =
                data?.current_song ??
                data?.currentSong ??
                null;

            if (nextSongData) {
                const song = createSongFromData(nextSongData);
                dispatch({ type: "SET_CURRENT_SONG", payload: song });
            }

            if (nextPlayingRaw !== undefined) {
                dispatch({ type: "SET_IS_PLAYING", payload: Boolean(nextPlayingRaw && (nextSongData || latestCurrentSongRef.current)) });
            }
        };

        const handleQueueUpdated = (data: any) => {
            if (!data?.songs || !Array.isArray(data.songs)) return;
            if (!acceptQueueRevision(data?.queue_revision, false, true)) return;
            const queuedSongs = data.songs.map(createSongFromData);
            const nextCurrentSongId =
                typeof data?.current_song_id === "string"
                    ? data.current_song_id
                    : typeof data?.currentSongId === "string"
                        ? data.currentSongId
                        : null;
            const inlineCurrentSongData = data?.current_song ?? data?.currentSong ?? null;
            const hasExplicitCurrentSong = Boolean(inlineCurrentSongData || nextCurrentSongId);

            if (shouldReconcileTransientEmptyQueue({
                queuedSongCount: queuedSongs.length,
                hasExplicitCurrentSong,
                hasLocalCurrentSong: Boolean(latestCurrentSongRef.current),
                isPlaying: latestSessionIsPlayingRef.current,
                previousSessionSongCount: latestSessionSongCountRef.current,
            })) {
                scheduleQueueReconciliation();
                return;
            }

            const nextCurrentSong = inlineCurrentSongData
                ? createSongFromData(inlineCurrentSongData)
                : nextCurrentSongId && latestCurrentSongRef.current && String(latestCurrentSongRef.current.id) === String(nextCurrentSongId)
                    ? latestCurrentSongRef.current
                    : null;

            const nextSongs = nextCurrentSong
                ? [
                    nextCurrentSong,
                    ...queuedSongs.filter((song) => String(song.id) !== String(nextCurrentSong.id))
                ]
                : queuedSongs;

            dispatch({ type: "SET_SONGS", payload: nextSongs });
        };

        const handleUserJoined = (data: any) => {
            logger.sampledDebug("session.participant.joined", {
                session_id: state.sessionId,
                participant_count: data?.user_count,
                is_host: latestSessionInfoRef.current?.is_host,
            });
            
            // Use backend user_count instead of local increment to prevent flickering
            const userCount = data?.user_count ?? (latestSessionInfoRef.current?.participants || 0) + 1;
            dispatch({
                type: "UPDATE_PARTICIPANTS",
                payload: userCount,
            });

            // Show notification to host when someone joins
            if (latestSessionInfoRef.current?.is_host && data?.username) {
                const joinerName = data.username;
                const isGuest = data.is_guest;
                const userLabel = isGuest ? `Guest ${joinerName}` : joinerName;
                
                toast({
                    title: "New Participant",
                    description: `${userLabel} joined your session`,
                    duration: 2000,
                });
            }
        };

        const handleUserLeft = (data?: any) => {
            // Use backend user_count when available to prevent flickering
            const userCount = data?.user_count ?? Math.max(1, (latestSessionInfoRef.current?.participants || 1) - 1);
            dispatch({ type: "UPDATE_PARTICIPANTS", payload: userCount });
            const actor = pickString(data?.username, data?.user_name, data?.name, data?.userName);
            if (actor) {
                addSessionActivity({
                    action: "left",
                    sessionId: state.sessionId || undefined,
                    playlistId: state.sessionInfo?.playlistId,
                    sessionTitle: state.currentPlaylist?.title || state.sessionInfo?.name || "Live Session",
                    actor,
                });
            }
        };

        const handleSessionEnded = () => {
            finalizeEndedSession({
                trackActivity: true,
            });
        };

        const handleUserBlocked = (data: any) => {
            const blockedUserId = pickString(
                data?.user_id,
                data?.userId,
                data?.target_user_id,
                data?.targetUserId
            );
            if (!blockedUserId) return;

            const storedGuest = parseStoredJson("guestUser");
            const currentUserId = pickString(
                user?.id,
                storedGuest?.id,
                storedGuest?.user_id,
                storedGuest?.userId
            );
            if (!currentUserId || String(currentUserId) !== String(blockedUserId)) {
                return;
            }

            const blockedSessionId = String(state.sessionId || "").trim();
            const blockedSessionTitle =
                String(
                    state.currentPlaylist?.title ||
                    state.currentPlaylist?.name ||
                    state.sessionInfo?.name ||
                    "Live Session"
                ).trim() || "Live Session";

            if (blockedSessionId) {
                markRecentlyJoinedPlaylistOffline(blockedSessionId);
                markSessionRecentlyLeft(blockedSessionId);
            }

            dispatch({ type: "CLEAR_SESSION" });
            clearSyncWithCurrentPlaylistContext();

            if (typeof window !== "undefined") {
                window.dispatchEvent(
                    new CustomEvent("qit-session-user-blocked", {
                        detail: {
                            sessionId: blockedSessionId,
                            sessionTitle: blockedSessionTitle,
                            blockedByUserId: pickString(
                                data?.blocked_by_user_id,
                                data?.blockedByUserId
                            ),
                            blockedByUsername: pickString(
                                data?.blocked_by_username,
                                data?.blockedByUsername
                            ),
                        },
                    })
                );
            }
        };

        const handleSettingsUpdate = (data: any) => {
            const updatedSettings =
                data?.settings ??
                data?.session_settings ??
                data?.sessionSettings ??
                data?.playback_settings ??
                data?.playbackSettings ??
                data;
            if (updatedSettings === undefined || updatedSettings === null) return;
            const normalizedSettings = normalizeSessionSettings(updatedSettings);
            const isHostNow = Boolean(latestSessionInfoRef.current?.is_host);
            const userInSession = Boolean(latestSessionInfoRef.current?.user_in_session ?? true);
            const nextCanEdit = Boolean(
                isHostNow ||
                (normalizedSettings.allow_guest_add && !normalizedSettings.require_approval)
            );
            const nextCanAddToQueue = Boolean(
                isHostNow ||
                (userInSession && normalizedSettings.allow_guest_add && !normalizedSettings.require_approval)
            );
            const nextCanSuggest = Boolean(
                !isHostNow &&
                userInSession &&
                (!normalizedSettings.allow_guest_add || normalizedSettings.require_approval)
            );
            dispatch({
                type: "UPDATE_SESSION_INFO",
                payload: {
                    settings: normalizedSettings,
                    can_edit: nextCanEdit,
                    can_add_to_queue: nextCanAddToQueue,
                    can_suggest: nextCanSuggest,
                },
            });
        };

        const handleSuggestionsCleared = () => {
            window.dispatchEvent(new Event("suggestions-cleared"));
        };

        const handleSessionCommentAdded = (data: any) => {
            const nextComments = Number(data?.comments_count ?? data?.commentsCount);
            if (Number.isFinite(nextComments)) {
                dispatch({
                    type: "UPDATE_PLAYLIST_STATS",
                    payload: {
                        comments: nextComments,
                        comment_count: nextComments,
                        comments_count: nextComments,
                    },
                });
            }
            window.dispatchEvent(
                new CustomEvent("session-comment-added", {
                    detail: data,
                })
            );
        };

        const handleSessionLikeUpdated = (data: any) => {
            const nextLikes = Number(
                data?.likes ??
                data?.like_count ??
                data?.likes_count ??
                data?.likeCount
            );
            const nextRatings = Number(
                data?.ratings ??
                data?.rating
            );
            if (Number.isFinite(nextLikes)) {
                dispatch({
                    type: "UPDATE_PLAYLIST_STATS",
                    payload: {
                        likes: nextLikes,
                        like_count: nextLikes,
                        likes_count: nextLikes,
                        ratings: Number.isFinite(nextRatings) ? nextRatings : undefined,
                        rating: Number.isFinite(nextRatings) ? nextRatings : undefined,
                    },
                });
            }

            window.dispatchEvent(
                new CustomEvent("session-like-updated", {
                    detail: data,
                })
            );
        };

        const handleHostChanged = (data: any) => {
            const nextHostId = data.new_host_id || data.newHostId;
            const becameHost = Boolean(
                nextHostId &&
                user?.id &&
                String(nextHostId) === String(user.id)
            );
            dispatch({
                type: "UPDATE_SESSION_INFO",
                payload: {
                    hostName: data.new_host_name || data.newHostName,
                    hostId: nextHostId,
                    is_host: becameHost
                },
            });
        };

        sessionChannel.bind('song-added', handleSongAdded);
        sessionChannel.bind('vote-updated', handleVoteUpdated);
        sessionChannel.bind('song-played', handleSongPlayed);
        sessionChannel.bind('playback-state', handlePlaybackState);
        sessionChannel.bind('queue-updated', handleQueueUpdated);
        sessionChannel.bind('user-joined', handleUserJoined);
        sessionChannel.bind('user-left', handleUserLeft);
        sessionChannel.bind('user-blocked', handleUserBlocked);
        sessionChannel.bind('session-ended', handleSessionEnded);
        sessionChannel.bind('session-updated', handleSettingsUpdate);
        sessionChannel.bind('host-changed', handleHostChanged);
        sessionChannel.bind('suggestions-cleared', handleSuggestionsCleared);
        sessionChannel.bind('session-comment-added', handleSessionCommentAdded);
        sessionChannel.bind('session-like-updated', handleSessionLikeUpdated);

        return () => {
            if (queueReconciliationTimer !== null) {
                window.clearTimeout(queueReconciliationTimer);
            }
            sessionChannel.unbind_all();
            pusher.unsubscribe(channelName);
        };
    }, [
        state.sessionId,
        pusher,
        state.sessionActive,
        finalizeEndedSession,
        user?.id,
    ]);

    // Session management functions
    const setSession = useCallback((playlist: Playlist, sessionId: string) => {
        const sessionInfo: SessionInfo = {
            id: sessionId,
            name: playlist.title,
            hostName: "You",
            hostId: "",
            is_host: true,
            songCount: playlist.songs?.length || 0,
            participants: 1,
            settings: DEFAULT_SESSION_SETTINGS,
        };

        dispatch({ type: "SET_SESSION", payload: { playlist, sessionId } });
        syncWithCurrentPlaylistContext(playlist, sessionId, sessionInfo);
    }, [syncWithCurrentPlaylistContext, user]);

    // NEW: Direct session data from backend
    const setFullSession = useCallback((sessionData: any) => {
        const applySessionData = (resolvedSessionData: any) => {
            const resolvedSessionId = String(resolvedSessionData?.session_id || "").trim();
            if (isCurrentUserBlockedFromSessionData(resolvedSessionData, user?.id)) {
                markSessionRecentlyLeft(resolvedSessionId);
                clearSyncWithCurrentPlaylistContext();
                return;
            }

            if (wasSessionRecentlyLeft(resolvedSessionId)) {
                clearSyncWithCurrentPlaylistContext();
                return;
            }

            const playlist = createPlaylistFromSessionData(resolvedSessionData);
            const sessionInfo = createSessionInfoFromData(resolvedSessionData, user?.id);
            const currentSong = resolvedSessionData.current_song
                ? createSongFromData(resolvedSessionData.current_song)
                : null;

            logger.debug("session.state.hydrated", {
                playlist_id: resolvedSessionData.playlist_id,
                songs_count: playlist.songs.length
            });

            dispatch({
                type: "SET_FULL_SESSION",
                payload: {
                    playlist,
                    sessionInfo,
                    sessionId: resolvedSessionId,
                    currentSong,
                    isPlaying: Boolean(resolvedSessionData.is_playing && currentSong),
                },
            });

            syncWithCurrentPlaylistContext(playlist, resolvedSessionId, sessionInfo);
        };

        applySessionData(sessionData);

        void (async () => {
            const token = getStoredAuthToken();
            const enrichedSessionData = await enrichSessionDataWithPlaylistTitle(sessionData, token);
            const enrichedSessionId = String(enrichedSessionData?.session_id || sessionData?.session_id || "").trim();
            if (isCurrentUserBlockedFromSessionData(enrichedSessionData, user?.id)) {
                markSessionRecentlyLeft(enrichedSessionId);
                clearSyncWithCurrentPlaylistContext();
                return;
            }

            if (wasSessionRecentlyLeft(enrichedSessionId)) {
                clearSyncWithCurrentPlaylistContext();
                return;
            }

            const initialTitle = resolvePlaylistTitleFromSessionData(sessionData);
            const enrichedTitle = resolvePlaylistTitleFromSessionData(enrichedSessionData);
            const initialImage = resolvePlaylistImageFromSessionData(sessionData);
            const enrichedImage = resolvePlaylistImageFromSessionData(enrichedSessionData);
            if (initialTitle !== enrichedTitle || initialImage !== enrichedImage) {
                applySessionData(enrichedSessionData);
            }
        })();
    }, [syncWithCurrentPlaylistContext, user?.id]);

    const clearSession = useCallback(() => {
        if (channel) {
            channel.unbind_all();
            setChannel(null);
        }
        if (state.sessionId && pusher) {
            pusher.unsubscribe(`session-${state.sessionId}`);
        }
        dispatch({ type: "CLEAR_SESSION" });
        clearSyncWithCurrentPlaylistContext();
    }, [state.sessionId, pusher, channel, clearSyncWithCurrentPlaylistContext]);

    const setCurrentSong = useCallback((song: Song | null) => {
        const canControlPlayback = Boolean(
            state.sessionInfo?.is_host ||
            (
                state.sessionInfo?.hostId &&
                user?.id &&
                String(state.sessionInfo.hostId) === String(user.id)
            )
        );
        if (!canControlPlayback) {
            logger.warn("session.playback.permission_denied", { action: "change_song" });
            return;
        }
        dispatch({ type: "SET_CURRENT_SONG", payload: song });
    }, [state.sessionInfo?.hostId, state.sessionInfo?.is_host, user?.id]);

    const setIsPlaying = useCallback((isPlaying: boolean) => {
        const canControlPlayback = Boolean(
            state.sessionInfo?.is_host ||
            (
                state.sessionInfo?.hostId &&
                user?.id &&
                String(state.sessionInfo.hostId) === String(user.id)
            )
        );
        if (!canControlPlayback) {
            logger.warn("session.playback.permission_denied", { action: "control_playback" });
            return;
        }
        dispatch({ type: "SET_IS_PLAYING", payload: isPlaying });
    }, [state.sessionInfo?.hostId, state.sessionInfo?.is_host, user?.id]);

    const setSessionActive = useCallback((active: boolean) => {
        dispatch({ type: "SET_SESSION_ACTIVE", payload: active });
        if (!active) {
            clearSession();
        }
    }, [clearSession]);

    const contextValue = useMemo(() => ({
        ...state,
        pusher,
        channel,
        setSession,
        setFullSession, // NEW
        clearSession,
        setCurrentSong,
        setIsPlaying,
        setSessionActive,
        loadFullSessionInfo,
    }), [
        state,
        pusher,
        channel,
        setSession,
        setFullSession,
        clearSession,
        setCurrentSong,
        setIsPlaying,
        setSessionActive,
        loadFullSessionInfo
    ]);

    const identityValue = useMemo(() => ({
        sessionId: state.sessionId,
        sessionActive: state.sessionActive,
        sessionInfo: state.sessionInfo,
    }), [state.sessionActive, state.sessionId, state.sessionInfo]);

    const queueValue = useMemo(() => ({
        currentPlaylist: state.currentPlaylist,
        currentSong: state.currentSong,
        isPlaying: state.isPlaying,
    }), [state.currentPlaylist, state.currentSong, state.isPlaying]);

    const playbackValue = useMemo(() => ({
        currentSong: state.currentSong,
        isPlaying: state.isPlaying,
    }), [state.currentSong, state.isPlaying]);

    const connectionValue = useMemo(() => ({
        pusher,
        channel,
        socket: undefined,
    }), [channel, pusher]);

    const actionsValue = useMemo(() => ({
        setSession,
        setFullSession,
        clearSession,
        setCurrentSong,
        setIsPlaying,
        setSessionActive,
        loadFullSessionInfo,
    }), [
        setSession,
        setFullSession,
        clearSession,
        setCurrentSong,
        setIsPlaying,
        setSessionActive,
        loadFullSessionInfo,
    ]);

    return (
        <SessionIdContext.Provider value={state.sessionId}>
            <SessionIdentityContext.Provider value={identityValue}>
                <SessionPlaylistContext.Provider value={state.currentPlaylist}>
                    <SessionPlaybackContext.Provider value={playbackValue}>
                        <SessionConnectionContext.Provider value={connectionValue}>
                            <SessionActionsContext.Provider value={actionsValue}>
                                <SessionQueueContext.Provider value={queueValue}>
                                    <SessionContext.Provider value={contextValue}>
                                        {children}
                                    </SessionContext.Provider>
                                </SessionQueueContext.Provider>
                            </SessionActionsContext.Provider>
                        </SessionConnectionContext.Provider>
                    </SessionPlaybackContext.Provider>
                </SessionPlaylistContext.Provider>
            </SessionIdentityContext.Provider>
        </SessionIdContext.Provider>
    );
};

export const useSession = (): SessionContextType => {
    const context = useContext(SessionContext);
    if (!context) {
        throw new Error("useSession must be used within SessionProvider");
    }
    return context;
};

export const useSessionIdentity = (): SessionIdentityState => {
    const context = useContext(SessionIdentityContext);
    if (!context) {
        throw new Error("useSessionIdentity must be used within SessionProvider");
    }
    return context;
};

export const useSessionQueue = (): SessionQueueState => {
    const context = useContext(SessionQueueContext);
    if (!context) {
        throw new Error("useSessionQueue must be used within SessionProvider");
    }
    return context;
};

export const useSessionId = (): string | null => {
    const context = useContext(SessionIdContext);
    if (context === undefined) {
        throw new Error("useSessionId must be used within SessionProvider");
    }
    return context;
};

export const useSessionPlaylist = (): Playlist | null => {
    const context = useContext(SessionPlaylistContext);
    if (context === undefined) {
        throw new Error("useSessionPlaylist must be used within SessionProvider");
    }
    return context;
};

export const useSessionPlayback = (): SessionPlaybackState => {
    const context = useContext(SessionPlaybackContext);
    if (!context) {
        throw new Error("useSessionPlayback must be used within SessionProvider");
    }
    return context;
};

export const useSessionConnection = (): SessionConnectionState => {
    const context = useContext(SessionConnectionContext);
    if (!context) {
        throw new Error("useSessionConnection must be used within SessionProvider");
    }
    return context;
};

export const useSessionActions = (): SessionActions => {
    const context = useContext(SessionActionsContext);
    if (!context) {
        throw new Error("useSessionActions must be used within SessionProvider");
    }
    return context;
};
